import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface StoryTestimonialProps {
  eyebrow?: string;
  headline?: string;
  body?: string;
  imageUrl?: string;
  quote?: string;
  quoteName?: string;
  stat1?: string;
  stat1Label?: string;
  stat2?: string;
  stat2Label?: string;
  reverse?: boolean;
}

function animateCounter(el: HTMLElement, target: string) {
  const match = target.match(/^(\d+(?:\.\d+)?)(.*)$/);
  if (!match || !match[1]) return;
  const num = parseFloat(match[1]);
  const suffix = match[2] ?? "";
  const proxy = { val: 0 };

  gsap.fromTo(proxy, { val: 0 }, {
    val: num,
    duration: 1.8,
    ease: "power2.out",
    onUpdate: function () { el.textContent = Math.round(proxy.val) + suffix; },
    scrollTrigger: { trigger: el, start: "top 85%", toggleActions: "play none none none" },
  });
}

export function StoryTestimonial({ props }: SectionComponentProps) {
  const {
    eyebrow = "Automotive Integrity",
    headline = "Restoring The Standard Of Care",
    body = "Frontline Auto Care was founded to solve a simple problem: getting your car detailed shouldn't require giving up your whole day. We're fully insured, background-checked, and equipped with industrial-grade products.",
    imageUrl = "",
    quote = "The level of detail is unlike anything I've experienced. They didn't just wash my car — they restored its soul right in my driveway.",
    quoteName = "Laura W. — Satisfied Customer",
    stat1 = "5",
    stat1Label = "Years of Master Craftsmanship",
    stat2 = "2K+",
    stat2Label = "Vehicles Serviced",
    reverse = false,
  } = props as StoryTestimonialProps;

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(".st-image",
        { filter: "grayscale(100%) brightness(0.7)", scale: 1.06 },
        { filter: "grayscale(0%) brightness(1)", scale: 1, duration: 1.4, ease: "power2.out",
          scrollTrigger: { trigger: ".st-image-panel", start: "top 80%", toggleActions: "play none none none" } }
      );

      gsap.fromTo(".st-quote-card", { opacity: 0, y: 32 }, {
        opacity: 1, y: 0, duration: 0.9, ease: "power3.out",
        scrollTrigger: { trigger: ".st-image-panel", start: "top 65%", toggleActions: "play none none none" },
      });

      gsap.fromTo(".st-image", { yPercent: -8 }, {
        yPercent: 8, ease: "none",
        scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: 2 },
      });

      const textTl = gsap.timeline({
        scrollTrigger: { trigger: ".st-text-panel", start: "top 78%", toggleActions: "play none none none" },
        defaults: { ease: "power3.out" },
      });

      textTl
        .fromTo(".st-eyebrow", { opacity: 0, x: -24 }, { opacity: 1, x: 0, duration: 0.5 })
        .fromTo(".st-headline-word", { yPercent: 105, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.75, stagger: 0.055 }, "-=0.2")
        .fromTo(".st-body", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.65 }, "-=0.35")
        .fromTo(".st-divider", { scaleX: 0, transformOrigin: "left center" }, { scaleX: 1, duration: 0.5 }, "-=0.3")
        .fromTo(".st-stat", { opacity: 0, y: 28, scale: 0.92 }, { opacity: 1, y: 0, scale: 1, duration: 0.6, stagger: 0.15 }, "-=0.15");

      const stat1El = el.querySelector<HTMLElement>(".st-stat-num-1");
      const stat2El = el.querySelector<HTMLElement>(".st-stat-num-2");
      if (stat1El && stat1) animateCounter(stat1El, stat1);
      if (stat2El && stat2) animateCounter(stat2El, stat2);
    }, el);

    return () => ctx.revert();
  }, [stat1, stat2]);

  const headlineWords = headline.split(" ");

  const imagePanel = (
    <div className="st-image-panel relative order-2 lg:order-1 overflow-hidden min-h-[480px] lg:min-h-0">
      {imageUrl ? (
        <img src={imageUrl} alt="" className="st-image absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="st-image absolute inset-0 bg-muted" />
      )}
      {/* Dark overlay — always dark so quote text stays legible against the photo */}
      <div className="absolute inset-0 bg-black/55" />
      <div className="st-quote-card absolute inset-0 flex items-center justify-center p-10 lg:p-14">
        <div className="max-w-md w-full">
          <svg className="w-9 h-9 text-primary mb-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1zm12 0c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
          </svg>
          {quote && (
            <p className="text-lg font-medium italic text-white leading-relaxed mb-6">"{quote}"</p>
          )}
          <div className="h-px w-10 bg-primary mb-4" />
          {quoteName && (
            <span className="text-[11px] font-bold uppercase tracking-widest text-white/70">{quoteName}</span>
          )}
        </div>
      </div>
    </div>
  );

  const textPanel = (
    <div className="st-text-panel flex flex-col justify-center gap-8 px-8 py-24 md:px-16 lg:px-24 order-1 lg:order-2 bg-background">
      {eyebrow && (
        <span className="st-eyebrow text-[11px] font-bold uppercase tracking-[0.24em] text-primary font-display">
          {eyebrow}
        </span>
      )}

      <h2 className="text-4xl md:text-5xl font-bold text-foreground leading-tight font-display uppercase">
        {headlineWords.map((w, i) => (
          <React.Fragment key={i}>
            <span className="inline-block overflow-hidden leading-[1.15] align-bottom">
              <span className="st-headline-word inline-block">{w}</span>
            </span>
            {i < headlineWords.length - 1 && " "}
          </React.Fragment>
        ))}
      </h2>

      {body && (
        <p className="st-body text-muted-foreground text-base leading-relaxed max-w-prose">{body}</p>
      )}

      <div className="st-divider h-px w-16 bg-primary" />

      <div className="grid grid-cols-2 gap-8">
        {stat1 && (
          <div className="st-stat">
            <span className="text-5xl font-bold text-foreground font-display tabular-nums block">
              <span className="st-stat-num-1">{stat1}</span><span className="text-primary">+</span>
            </span>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest leading-tight block mt-2">
              {stat1Label}
            </span>
          </div>
        )}
        {stat2 && (
          <div className="st-stat">
            <span className="text-5xl font-bold text-foreground font-display tabular-nums block">
              <span className="st-stat-num-2">{stat2}</span>
            </span>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest leading-tight block mt-2">
              {stat2Label}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <section ref={sectionRef} className="grid lg:grid-cols-2 min-h-[70vh] bg-background" id="story">
      {reverse ? <>{textPanel}{imagePanel}</> : <>{imagePanel}{textPanel}</>}
    </section>
  );
}
